﻿import {
  createQuery,
  keepPreviousData,
  useQueryClient,
} from "@tanstack/solid-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/solid-router";
import { createSignal, onMount, Show } from "solid-js";
import { toast } from "solid-sonner";
import { z } from "zod";
import {
  DataTable,
  type ColumnDefExtended,
} from "~/components/data-table/data-table";
import {
  IconArrowRight,
  IconArrowRightFromLine,
  IconCircleDollarSign,
  IconLink,
  IconPencil,
  IconTrash,
} from "~/components/icons";
import { JournalEntryLinesEditor } from "~/components/journal-entry/journal-entry-lines-editor";
import { NotFound } from "~/components/not-found";
import { StandardRouteAssociationsPanel } from "~/components/standard-route/detail/associations-panel";
import { StandardRouteDetailCard } from "~/components/standard-route/detail/detail-card";
import { StandardRouteDetailEditCard } from "~/components/standard-route/detail/edit-card";
import { StandardRouteDetailHeader } from "~/components/standard-route/detail/header";
import {
  StandardRouteDetailGrid,
  StandardRouteDetailLeftColumn,
  StandardRouteDetailMode,
  StandardRouteDetailRightCard,
  StandardRouteDetailRightCardHeader,
  StandardRouteDetailRightColumn,
  StandardRouteDetailTabs,
  StandardRouteDetailTabsContent,
  StandardRouteDetailTabsStack,
} from "~/components/standard-route/detail/layout";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { CardContent } from "~/components/ui/card";
import { DatePickerField } from "~/components/ui/date-picker-field";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "~/components/ui/dropdown-menu";
import { Col, Grid } from "~/components/ui/grid";
import { MultiSelect } from "~/components/ui/multi-select";
import { Separator } from "~/components/ui/separator";
import { TabsList, TabsTrigger } from "~/components/ui/tabs";
import {
  TextField,
  TextFieldErrorMessage,
  TextFieldInput,
  TextFieldLabel,
} from "~/components/ui/text-field";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { AccountsAllResource } from "~/features/account/account.resource";
import { prefetchAssociations } from "~/features/association/association.prefetch";
import {
  BankTransactionDetailResource,
  EMPTY_BANK_TRANSACTION,
} from "~/features/bank-transaction/bank-transaction.resource";
import {
  businessDetailResource,
  businessesResource,
} from "~/features/business/business.resource";
import { getJournalEntryDetailPageData } from "~/features/journal-entry/journal-entry-detail-page";
import { deleteJournalEntry } from "~/features/journal-entry/journal-entry.api";
import { useJournalEntryForm } from "~/features/journal-entry/journal-entry.form";
import {
  invalidateJournalEntryDependentQueries,
  JournalEntryDetailResource,
} from "~/features/journal-entry/journal-entry.resource";
import type {
  JournalEntryItem,
  JournalEntryLineItem,
} from "~/features/journal-entry/journal-entry.server";
import { withBusinessGuard } from "~/lib/route-guards";
import { extractErrorMessage } from "~/lib/server-fn-error";
import { formatCurrency, formatDisplayCalendarDate } from "~/lib/utils";

// -- Line columns -----------------------------------------------------------

const formatUsd = (v: number | null) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(v ?? 0);

const lineColumns: ColumnDefExtended<JournalEntryLineItem, unknown>[] = [
  {
    id: "filter",
    accessorFn: (row) =>
      [row.accountCode, row.accountName, row.description]
        .filter(Boolean)
        .join(" "),
    enableHiding: false,
    enableSorting: false,
    header: () => null,
    cell: () => null,
    defaultHidden: true,
  },
  {
    accessorKey: "accountCode",
    header: () => <div class="pl-4">Account</div>,
    cellClass: "pl-4",
    footerClass: "pl-4",
    cell: (props) => {
      const entry = props.row.original;
      const label = entry.accountCode
        ? `${entry.accountCode} - ${entry.accountName}`
        : entry.accountId;
      return (
        <Link
          to="/dashboard/accounts/$accountId"
          params={{ accountId: entry.accountId }}
          class="hover:underline"
        >
          {label}
        </Link>
      );
    },
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "debit",
    header: () => <div class="text-right">Debit</div>,
    cell: (props) => (
      <div class="text-right font-mono">
        {props.row.original.debit != null
          ? formatUsd(props.row.original.debit)
          : ""}
      </div>
    ),
    enableSorting: false,
    enableSum: true,
    sumFn: (v) => formatUsd(v),
  },
  {
    accessorKey: "credit",
    header: () => <div class="text-right pr-2 lg:pr-0">Credit</div>,
    footerClass: "pr-4 lg:pr-2",
    cell: (props) => (
      <div class="text-right font-mono pr-2 lg:pr-0">
        {props.row.original.credit != null
          ? formatUsd(props.row.original.credit)
          : ""}
      </div>
    ),
    enableSorting: false,
    enableSum: true,
    sumFn: (v) => formatUsd(v),
  },
  {
    accessorKey: "description",
    header: "Memo",
    cell: (props) => props.row.original.description ?? "",
    enableSorting: false,
    defaultMobileHidden: true,
  },
];

export const Route = createFileRoute(
  "/_dashboard/dashboard/journal-entries/$journalEntryId",
)({
  staticData: {
    breadcrumb: (m) =>
      m.loaderData?.journalEntry?.description ?? "Journal Entry",
  },
  validateSearch: z.object({
    mode: z.enum(["edit"]).optional(),
    prefillDescription: z.string().optional(),
    prefillDate: z.string().optional(),
    prefillAmount: z.number().optional(),
    prefillDocumentId: z.string().optional(),
  }),
  // Route-specific 404 message (takes precedence over the dashboard-wide
  // notFoundComponent). Shown when a journal entry doesn't exist — e.g. it was
  // deleted, or an AI agent linked here by mistake.
  notFoundComponent: () => (
    <NotFound
      title="Journal entry not found"
      message="This journal entry doesn't exist. It may have been deleted, or an agent may have linked you here by mistake."
      homeTo="/dashboard/journal-entries"
      homeLabel="Go to journal entries"
    />
  ),
  ...withBusinessGuard(),
  loader: async ({ params, context }) => {
    const collection = await context.queryClient.ensureQueryData(
      businessesResource.options(undefined),
    );
    const businessId = collection.activeBusinessId!;

    const [activeBusiness, journalEntry, allAccounts] = await Promise.all([
      context.queryClient.ensureQueryData(
        businessDetailResource.options(businessId),
      ),
      params.journalEntryId !== "new"
        ? getJournalEntryDetailPageData({
            data: {
              businessId,
              journalEntryId: params.journalEntryId,
            },
          }).then((data) => {
            // Seed the resource cache so the detail query resolves from cache
            // on first render (mirrors the document detail page pattern).
            JournalEntryDetailResource.seed(
              context.queryClient,
              { businessId, id: params.journalEntryId },
              data.journalEntry,
            );
            return data.journalEntry;
          })
        : (context.queryClient.setQueryData(
            JournalEntryDetailResource.key({
              businessId,
              id: params.journalEntryId,
            }),
            null,
          ),
          null),
      context.queryClient.ensureQueryData(
        AccountsAllResource.options({ businessId }),
      ),
    ]);

    // Prefetch the Associations panel queries so they resolve from cache on
    // mount instead of flashing loading states. Fire-and-forget (not awaited)
    // so it never blocks the loader's critical path or SSR.
    if (params.journalEntryId !== "new") {
      prefetchAssociations(
        context.queryClient,
        businessId,
        "journal-entry",
        params.journalEntryId,
      );
    }

    // Bank-sourced JEs render a compact summary of their originating bank
    // transaction (BankTransactionDetailResource). Prefetch it so the
    // "Loading transaction…" state doesn't flash on mount — this query isn't
    // covered by prefetchAssociations (that only covers the association list
    // and tab-list resources). Fire-and-forget, same as above.
    if (journalEntry?.source === "bank" && journalEntry.bankTransactionId) {
      void context.queryClient.prefetchQuery(
        BankTransactionDetailResource.options({
          businessId,
          id: journalEntry.bankTransactionId,
        }),
      );
    }

    return { businessId, activeBusiness, journalEntry, allAccounts };
  },
  component: JournalEntryDetailPage,
});

type Mode = "view" | "edit";

// -- Page ----------------------------------------------------------------------

function JournalEntryDetailPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const params = Route.useParams();
  const data = Route.useLoaderData();
  const search = Route.useSearch();

  businessDetailResource.seedIfEmpty(
    qc,
    data().businessId,
    data().activeBusiness,
  );
  AccountsAllResource.seedIfEmpty(
    qc,
    { businessId: data().businessId },
    data().allAccounts,
  );
  if (data().journalEntry) {
    JournalEntryDetailResource.seedIfEmpty(
      qc,
      { businessId: data().businessId, id: params().journalEntryId },
      data().journalEntry!,
    );
  } else if (params().journalEntryId === "new") {
    // Seed null into QueryClient for "new" to prevent SSR deferred stream hang
    const newKey = JournalEntryDetailResource.key({
      businessId: data().businessId,
      id: params().journalEntryId,
    });
    if (qc.getQueryData(newKey) === undefined) {
      qc.setQueryData(newKey, null);
    }
  }

  const isNew = () => params().journalEntryId === "new";

  // Build prefill from search params (used when navigating from a document, etc.)
  const prefill = () => {
    const s = search();
    if (
      !s.prefillDocumentId &&
      !s.prefillDescription &&
      !s.prefillAmount &&
      !s.prefillDate
    )
      return undefined;

    const lines: {
      accountId: string;
      debit: string;
      credit: string;
      description: string;
    }[] = [];
    const amount = s.prefillAmount ? s.prefillAmount : null;

    if (amount != null && !isNaN(amount)) {
      // Create one debit and one credit line for the amount
      lines.push({
        accountId: "",
        debit: amount.toFixed(2),
        credit: "",
        description: s.prefillDescription || "",
      });
      lines.push({
        accountId: "",
        debit: "",
        credit: amount.toFixed(2),
        description: "",
      });
    } else {
      // Fallback: two empty lines
      lines.push({ accountId: "", debit: "", credit: "", description: "" });
      lines.push({ accountId: "", debit: "", credit: "", description: "" });
    }

    const result: Partial<
      import("~/features/journal-entry/journal-entry.schema").JournalEntryForm
    > = {
      lines,
      documentIds: s.prefillDocumentId ? [s.prefillDocumentId] : [],
    };
    if (s.prefillDescription) result.description = s.prefillDescription;
    if (s.prefillDate) result.date = s.prefillDate;

    return result;
  };

  const initialMode: Mode = search().mode === "edit" ? "edit" : "view";
  const [mode, setMode] = createSignal<Mode>(initialMode);

  onMount(() => {
    if (initialMode === "edit") {
      const url = new URL(window.location.href);
      url.searchParams.delete("mode");
      window.history.replaceState(window.history.state, "", url.toString());
    }
  });

  const journalEntryQuery = createQuery(() => ({
    ...JournalEntryDetailResource.options(
      { businessId: data().businessId, id: params().journalEntryId },
      !isNew(),
    ),
    placeholderData: keepPreviousData,
  }));

  const accountsQuery = AccountsAllResource.use(() => ({
    businessId: data().businessId,
  }));
  const allAccounts = () => accountsQuery.data ?? [];

  const businessQuery = createQuery(() => ({
    ...businessDetailResource.options(data().businessId),
    placeholderData: keepPreviousData,
  }));
  const classes = () => businessQuery.data?.classes ?? [];

  return (
    <div class="flex flex-1 flex-col px-4 sm:px-6 pb-3 pt-10">
      <Show when={isNew()}>
        <JournalEntryEdit
          journalEntry={null}
          businessId={data().businessId}
          allAccounts={allAccounts()}
          classes={classes()}
          prefill={prefill()}
          onCancel={() => navigate({ to: "/dashboard/journal-entries" })}
          onSaved={() => navigate({ to: "/dashboard/journal-entries" })}
        />
      </Show>
      <Show when={!isNew()}>
        <Show
          when={journalEntryQuery.data}
          fallback={
            <div role="status" class="w-full animate-pulse">
              <div class="h-150 bg-muted rounded-lg w-full" />
            </div>
          }
        >
          {(je) => (
            <Show
              when={mode() === "edit" && je().source !== "bank"}
              fallback={
                <JournalEntryView
                  journalEntry={je()}
                  businessId={data().businessId}
                  allAccounts={allAccounts()}
                  classes={classes()}
                  setMode={setMode}
                  onDeleted={() => {
                    JournalEntryDetailResource.remove(qc, {
                      businessId: data().businessId,
                      id: params().journalEntryId,
                    });
                    // Remove the JE list cache so the table doesn't flash the
                    // deleted row before a background refetch; derived
                    // account/overview/association data is still invalidated.
                    qc.removeQueries({
                      queryKey: ["journal-entry", data().businessId],
                    });
                    void invalidateJournalEntryDependentQueries(
                      qc,
                      data().businessId,
                    );
                    navigate({ to: "/dashboard/journal-entries" });
                  }}
                />
              }
            >
              <JournalEntryEdit
                journalEntry={je()}
                businessId={data().businessId}
                allAccounts={allAccounts()}
                classes={classes()}
                onCancel={() => setMode("view")}
                onSaved={() => {
                  JournalEntryDetailResource.invalidate(qc, {
                    businessId: data().businessId,
                    id: params().journalEntryId,
                  });
                  // The form already optimistically updated the JE list; only
                  // the derived account/overview/association data needs a
                  // refetch here.
                  void invalidateJournalEntryDependentQueries(
                    qc,
                    data().businessId,
                  );
                  setMode("view");
                }}
              />
            </Show>
          )}
        </Show>
      </Show>
    </div>
  );
}

// -- Delete Dialog -------------------------------------------------------------

function DeleteJournalEntryDialog(props: {
  journalEntry: JournalEntryItem;
  businessId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted: () => void;
}) {
  const [isDeleting, setIsDeleting] = createSignal(false);

  async function handleDelete() {
    setIsDeleting(true);
    try {
      await deleteJournalEntry({
        data: { id: props.journalEntry.id, businessId: props.businessId },
      });
      props.onDeleted();
    } catch {
      toast.error("Failed to delete journal entry");
    } finally {
      setIsDeleting(false);
      props.onOpenChange(false);
    }
  }

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent class="max-w-md">
        <DialogHeader>
          <DialogTitle>Delete Journal Entry</DialogTitle>
          <DialogDescription>
            This will permanently delete{" "}
            <span class="font-semibold">
              "{props.journalEntry.description}"
            </span>{" "}
            and all its lines. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => props.onOpenChange(false)}
            disabled={isDeleting()}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting()}
          >
            <IconTrash class="mr-2 size-4" />
            {isDeleting() ? "Deleting..." : "Delete Journal Entry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -- View ----------------------------------------------------------------------

function JournalEntryView(props: {
  journalEntry: JournalEntryItem;
  businessId: string;
  allAccounts: { id: string; code: string; name: string }[];
  classes: { id: string; name: string }[];
  setMode: (m: Mode) => void;
  onDeleted: () => void;
}) {
  const [deleteOpen, setDeleteOpen] = createSignal(false);
  const je = () => props.journalEntry;
  const isBankSourced = () => je().source === "bank";

  const totalDebits = () => je().lines.reduce((s, e) => s + (e.debit ?? 0), 0);

  // Fetch the linked bank transaction so we can show a compact summary under
  // the "Transaction" section header (mirrors the allocations group style).
  // For non-bank JEs the query is disabled; fall back to the empty placeholder
  // (rather than keepPreviousData's `undefined`) so the disabled query doesn't
  // suspend SSR and spin the browser tab forever on hard refresh.
  const bankTransactionQuery = createQuery(() => ({
    ...BankTransactionDetailResource.options(
      { businessId: props.businessId, id: je().bankTransactionId! },
      isBankSourced() && !!je().bankTransactionId,
    ),
    placeholderData: (previousData) => previousData ?? EMPTY_BANK_TRANSACTION,
  }));

  return (
    <div class="flex flex-1 flex-col gap-6">
      <StandardRouteDetailHeader
        icon={<IconCircleDollarSign class="size-5" />}
        title={je().description}
        badge={je().source === "bank" ? "Bank" : "Manual"}
        date={je().date ?? undefined}
        menuContent={
          <>
            <Show when={!isBankSourced()}>
              <DropdownMenuItem onClick={() => props.setMode("edit")}>
                <IconPencil class="size-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                class="text-destructive focus:text-destructive"
                onClick={() => setDeleteOpen(true)}
              >
                <IconTrash class="size-4" />
                Delete
              </DropdownMenuItem>
            </Show>
          </>
        }
      />

      <StandardRouteDetailMode mode="content">
        <StandardRouteDetailGrid>
          {/* Left: summary */}
          <StandardRouteDetailLeftColumn>
            <StandardRouteDetailCard
              icon={<IconArrowRightFromLine class="size-5" />}
              title="Journal Entry"
              contentClass=""
              action={
                <Show when={!isBankSourced()}>
                  <Tooltip>
                    <TooltipTrigger
                      as={Button<"button">}
                      variant="ghost"
                      size="icon"
                      class="size-7 text-muted-foreground hover:text-foreground"
                      aria-label="Edit journal entry"
                      onClick={() => props.setMode("edit")}
                    >
                      <IconPencil class="size-4" />
                    </TooltipTrigger>
                    <TooltipContent>Edit</TooltipContent>
                  </Tooltip>
                </Show>
              }
            >
              {/* Total is the single most important accounting fact — make it the hero */}
              <div class="pb-5">
                <p class="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">
                  Total
                </p>
                <p class="mt-1.5 text-3xl font-semibold tabular-nums tracking-tight">
                  {formatUsd(totalDebits())}
                </p>
              </div>

              <div class="divide-y divide-border border-t">
                <InfoRow
                  label="Date"
                  value={formatDisplayCalendarDate(je().date)}
                />
                <InfoRow label="Reference" value={je().reference ?? null} />
                <InfoRow
                  label="Source"
                  value={je().source === "bank" ? "Bank" : "Manual"}
                />
              </div>

              <Show when={props.classes?.length > 0 && je().classId}>
                <div class="pt-5">
                  <p class="mb-2 text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">
                    Class
                  </p>
                  <div class="flex flex-wrap gap-1.5">
                    <Badge variant="outline" class="font-normal">
                      {props.classes.find((c) => c.id === je().classId)?.name ??
                        null}
                    </Badge>
                  </div>
                </div>
              </Show>

              {/* Linked bank transaction — styled like the allocations group
                  in the bank transaction detail card: a section header with
                  the label on the left and the link on the right, plus a
                  compact summary of the transaction below. */}
              <Show when={je().source === "bank" && je().bankTransactionId}>
                <div class="pt-5">
                  <div class="mb-2 flex items-center justify-between gap-2">
                    <p class="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">
                      Bank Transaction
                    </p>
                    <Link
                      to="/dashboard/bank-transactions/$transactionId"
                      params={{ transactionId: je().bankTransactionId! }}
                      class="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                    >
                      Bank Transaction
                      <IconArrowRight class="size-3.5" />
                    </Link>
                  </div>
                  <Show
                    when={bankTransactionQuery.data}
                    fallback={
                      <p class="text-sm text-muted-foreground">
                        Loading transaction…
                      </p>
                    }
                  >
                    {(bt) => (
                      <div class="space-y-2">
                        <div class="flex items-center justify-between gap-3">
                          <span class="min-w-0 truncate text-sm font-medium text-foreground">
                            {[bt().institutionName, bt().bankAccountName]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                          <span class="shrink-0 font-mono text-sm tabular-nums text-foreground">
                            {formatCurrency(bt().amount, {
                              currency: bt().currencyCode,
                            })}
                          </span>
                        </div>
                      </div>
                    )}
                  </Show>
                </div>
              </Show>
            </StandardRouteDetailCard>
          </StandardRouteDetailLeftColumn>

          <StandardRouteDetailRightColumn>
            <StandardRouteDetailRightCard>
              <StandardRouteDetailRightCardHeader>
                <StandardRouteDetailTabs
                  defaultValue="lines"
                  class="flex flex-col"
                >
                  <TabsList class="w-full">
                    <TabsTrigger value="lines" class="flex-1">
                      <IconCircleDollarSign class="mr-2 size-4" />
                      <span class="hidden sm:inline">Lines</span>
                    </TabsTrigger>
                    <TabsTrigger value="associations" class="flex-1">
                      <IconLink class="mr-2 size-4" />
                      <span class="hidden sm:inline">Associations</span>
                    </TabsTrigger>
                  </TabsList>

                  <StandardRouteDetailTabsStack>
                    <StandardRouteDetailTabsContent
                      value="lines"
                      class="overflow-auto p-1 -m-1"
                      forceMount
                    >
                      <DataTable
                        data={je().lines}
                        columns={lineColumns}
                        defaultShowAllRows
                        defaultVisibility={{ filter: false }}
                        hidePagination
                      />
                    </StandardRouteDetailTabsContent>

                    <StandardRouteDetailTabsContent
                      value="associations"
                      class="overflow-auto"
                      forceMount
                    >
                      <CardContent class="px-0">
                        <StandardRouteAssociationsPanel
                          source={{ type: "journal-entry", id: je().id }}
                          businessId={props.businessId}
                          tabs={[
                            {
                              type: "bank-account",
                              defaultVisibility: {
                                bankName: false,
                                accountCode: false,
                                description: false,
                                updatedAt: false,
                              },
                            },
                            {
                              type: "bank-transaction",
                              defaultVisibility: {
                                status: false,
                                institutionName: false,
                                mappedAccountCode: false,
                                bankAccountName: false,
                                updatedAt: false,
                              },
                            },
                            {
                              type: "document",
                              defaultVisibility: {
                                type: false,
                                vendor: false,
                                amount: false,
                              },
                            },
                            {
                              type: "fixed-asset",
                              defaultVisibility: {
                                purchaseDate: false,
                                depreciationMethod: false,
                                purchaseCost: false,
                                updatedAt: false,
                              },
                            },
                            {
                              type: "worksheet",
                              defaultVisibility: {
                                description: false,
                                type: false,
                                updatedAt: false,
                              },
                            },
                            {
                              type: "vendor",
                              defaultVisibility: {
                                address: false,
                                website: false,
                                contactName: false,
                              },
                            },
                          ]}
                        />
                      </CardContent>
                    </StandardRouteDetailTabsContent>
                  </StandardRouteDetailTabsStack>
                </StandardRouteDetailTabs>
              </StandardRouteDetailRightCardHeader>
            </StandardRouteDetailRightCard>
          </StandardRouteDetailRightColumn>
        </StandardRouteDetailGrid>
      </StandardRouteDetailMode>

      <DeleteJournalEntryDialog
        journalEntry={je()}
        businessId={props.businessId}
        open={deleteOpen()}
        onOpenChange={setDeleteOpen}
        onDeleted={props.onDeleted}
      />
    </div>
  );
}

// -- InfoRow helper ------------------------------------------------------------

function InfoRow(props: { label: string; value: string | null | undefined }) {
  return (
    <div class="flex items-center justify-between py-3.5">
      <p class="text-sm text-muted-foreground">{props.label}</p>
      <h6 class="text-sm font-semibold">{props.value ?? "-"}</h6>
    </div>
  );
}

// -- Edit / Create -------------------------------------------------------------

function JournalEntryEdit(props: {
  journalEntry: JournalEntryItem | null;
  businessId: string;
  allAccounts: { id: string; code: string; name: string }[];
  classes: { id: string; name: string }[];
  onCancel: () => void;
  onSaved: () => void;
  prefill?: Partial<
    import("~/features/journal-entry/journal-entry.schema").JournalEntryForm
  >;
}) {
  const isNew = () => props.journalEntry === null;
  const [serverError, setServerError] = createSignal("");

  const form = useJournalEntryForm(props.businessId, props.journalEntry, {
    prefill: props.prefill,
    onSuccess: () => {
      toast.success(
        isNew()
          ? "Journal entry created successfully"
          : "Journal entry updated successfully",
      );
      props.onSaved();
    },
    onError: (e) =>
      setServerError(extractErrorMessage(e, "An unexpected error occurred")),
  });

  const accountOptions = () =>
    props.allAccounts.map((a) => ({
      label: `${a.code} - ${a.name}`,
      value: a.id,
    }));

  return (
    <StandardRouteDetailEditCard
      title={isNew() ? "Add Journal Entry" : "Edit Journal Entry"}
      description={
        isNew()
          ? "Record a new journal entry."
          : "Update the journal entry details. Changes apply immediately on save."
      }
      formId="journal-entry-edit-form"
      onCancel={props.onCancel}
      saveButton={
        <form.Subscribe
          selector={(s) => ({
            canSubmit: s.canSubmit,
            isSubmitting: s.isSubmitting,
          })}
        >
          {(state) => (
            <Button
              type="submit"
              form="journal-entry-edit-form"
              disabled={!state().canSubmit || state().isSubmitting}
            >
              {state().isSubmitting
                ? "Saving..."
                : isNew()
                  ? "Create Journal Entry"
                  : "Save Changes"}
            </Button>
          )}
        </form.Subscribe>
      }
    >
      <form
        id="journal-entry-edit-form"
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <div class="space-y-6">
          {/* Header fields */}
          <Grid cols={1} colsMd={2} colsLg={2} class="gap-4">
            <Col>
              <form.Field name="date">
                {(field) => (
                  <DatePickerField
                    label="Date"
                    required
                    value={field().state.value}
                    onChange={field().handleChange}
                    onBlur={field().handleBlur}
                    error={field().state.meta.errors[0]?.toString()}
                  />
                )}
              </form.Field>
            </Col>
            <Col>
              <form.Field name="description">
                {(field) => (
                  <TextField>
                    <TextFieldLabel>
                      Description <span class="text-destructive">*</span>
                    </TextFieldLabel>
                    <TextFieldInput
                      value={field().state.value}
                      onBlur={field().handleBlur}
                      onInput={(e) =>
                        field().handleChange(e.currentTarget.value)
                      }
                      placeholder="e.g. Office supplies expense"
                    />
                    <Show when={field().state.meta.errors.length > 0}>
                      <TextFieldErrorMessage>
                        {field().state.meta.errors[0]?.toString()}
                      </TextFieldErrorMessage>
                    </Show>
                  </TextField>
                )}
              </form.Field>
            </Col>
            <Col>
              <form.Field name="reference">
                {(field) => (
                  <TextField>
                    <TextFieldLabel>Reference</TextFieldLabel>
                    <TextFieldInput
                      value={field().state.value ?? ""}
                      onBlur={field().handleBlur}
                      onInput={(e) =>
                        field().handleChange(e.currentTarget.value || undefined)
                      }
                      placeholder="e.g. INV-001"
                    />
                  </TextField>
                )}
              </form.Field>
            </Col>
            <Show when={props.classes.length > 0}>
              <Col>
                <form.Field name="classId">
                  {(field) => (
                    <TextField>
                      <TextFieldLabel>Class</TextFieldLabel>
                      <MultiSelect
                        placeholder="Select class..."
                        searchPlaceholder="Search classes..."
                        options={props.classes.map((c) => ({
                          label: c.name,
                          value: c.id,
                        }))}
                        value={
                          field().state.value
                            ? [field().state.value as string]
                            : []
                        }
                        onChange={(vals) =>
                          field().handleChange(vals[0] ?? undefined)
                        }
                        multiple={false}
                      />
                    </TextField>
                  )}
                </form.Field>
              </Col>
            </Show>
          </Grid>

          <Separator />

          {/* Lines */}
          <JournalEntryLinesEditor
            Field={form.Field}
            accountOptions={accountOptions()}
          />

          {/* Server error */}
          <Show when={serverError()}>
            <p class="text-sm text-destructive">{serverError()}</p>
          </Show>
        </div>
      </form>
    </StandardRouteDetailEditCard>
  );
}
